import { useCallback, useContext, useEffect, useMemo } from 'react';
import log from '@/lib/logger';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ErrorBoundary } from '@sentry/react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import OnboardingButton from 'components/OnboardingButton';
import SectionTitle from 'components/SectionTitle';
import ViewSectionMessage from 'components/ViewSectionMessage';
import { useAppDispatch, useAppSelector } from 'features';
import { assetsIsFetching, getAllAssetsById } from 'features/assets/selectors';
import { fetchAssets } from 'features/assets/thunks';
import { fetchChargePoints, unselectChargePoint, updateChargePoint } from 'features/ChargePoints';
import {
  fetchConnectorsByGridConnectionId,
  fetchOnlineConnectorsByGridConnectionId,
  fetchUpdatedConnector,
  unselectConnector,
} from 'features/Connectors';
import { getConnectorById } from 'features/selectors';
import { useFirstRender } from 'hooks/useFirstRender';
import useOnboarding from 'hooks/useOnboarding';
import { shallowEqual } from 'react-redux';
import { type Asset, type ChargePoint, type Connector, type GridConnection } from 'types/swagger';

interface WebSocketMessage {
  type?: string;
  message?: string | boolean;
}

interface MessageData {
  data?: unknown;
}

interface SampledValueData {
  connector?: string | number;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return null;
  }
}

const Title = () => (
  <div className="d-flex justify-content-between align-items-center mb-3">
    <SectionTitle className="w-100" title="Monitor de empalmes" />
    <OnboardingButton />
  </div>
);

export const ChargeCenterLayout = () => {
  const dispatch = useAppDispatch();
  const firstRender = useFirstRender();
  const { user, synclogWebsocket } = useContext(UserSessionContext);
  const assets = useAppSelector(getAllAssetsById, shallowEqual) as unknown as Asset[];
  const assetsLoading = useAppSelector(assetsIsFetching);
  const chargePointsByGridConnectionId = useAppSelector(
    (state) => state.chargePoints.byGridConnectionId,
    shallowEqual
  );

  const gridConnections = useMemo(
    () => assets?.map((asset) => asset.grid_connections as unknown as GridConnection).flat(),
    [assets]
  );

  useOnboarding(
    assetsLoading === 'success' || assetsLoading === 'error' ? 'charge_center_index' : '',
    null,
    null,
    800
  );

  const handleCloseDrawer = useCallback(() => {
    dispatch(unselectConnector());
    dispatch(unselectChargePoint());
  }, [dispatch]);

  const fetchChargeCenterConnectors = useCallback(async () => {
    await dispatch(fetchConnectorsByGridConnectionId(gridConnections[0].id));
    await dispatch(fetchOnlineConnectorsByGridConnectionId(gridConnections[0].id));
    gridConnections.forEach((gC, i) => {
      if (i === 0) return;
      void dispatch(fetchConnectorsByGridConnectionId(gC.id));
      void dispatch(fetchOnlineConnectorsByGridConnectionId(gC.id));
    });
  }, [dispatch, gridConnections]);

  const fetchChargeCenterData = useCallback(async () => {
    await dispatch(fetchAssets());
    await dispatch(fetchChargePoints());
  }, [dispatch]);

  const navigate = useNavigate();
  const location = useLocation();

  const currentTab: 'charge-center' | 'map' = location.pathname.includes('/charge-center/map')
    ? 'map'
    : 'charge-center';

  useEffect(() => {
    if (gridConnections?.length) void fetchChargeCenterConnectors();
  }, [gridConnections.length, fetchChargeCenterConnectors]);

  useEffect(() => {
    window.addEventListener('popstate', handleCloseDrawer);
    return () => {
      window.removeEventListener('popstate', handleCloseDrawer);
    };
  }, [handleCloseDrawer]);

  useEffect(() => {
    if (!firstRender) return;
    void fetchChargeCenterData();
  }, [dispatch, fetchChargeCenterData, firstRender]);

  useEffect(() => {
    if (synclogWebsocket) {
      const newWebsocket = synclogWebsocket;
      if (newWebsocket.readyState === 1 && firstRender) {
        const subsMsg = {
          type: 'subscribe',
          topic: 'synclog',
        };
        newWebsocket.send(JSON.stringify(subsMsg));
      }
      newWebsocket.onmessage = async (event: MessageEvent) => {
        if (typeof event.data !== 'string') return;

        const json = safeJsonParse(event.data) as WebSocketMessage | null;
        if (!json) return;

        if (json.type === 'authorization' && json.message === false) {
          newWebsocket.close();
          return;
        }

        const message = json.message;
        if (typeof message !== 'string') return;

        if (
          message.includes('/chargepoints/charge_points/') &&
          (message.includes('update') || message.includes('created'))
        ) {
          const messageData = safeJsonParse(message) as MessageData | null;
          if (messageData?.data) {
            dispatch(updateChargePoint(messageData.data as ChargePoint));
          }
          return;
        }

        if (
          message.includes('/chargepoints/connectors/') &&
          (message.includes('update') || message.includes('created'))
        ) {
          const messageData = safeJsonParse(message) as MessageData | null;
          if (messageData?.data) {
            void dispatch(fetchUpdatedConnector(messageData.data as Connector));
          }
          return;
        }

        if (
          message.includes('/chargepoints/sampled_values/') &&
          (message.includes('update') || message.includes('created'))
        ) {
          const messageData = safeJsonParse(message) as MessageData | null;
          const sampledValue = messageData?.data as SampledValueData | undefined;
          const connectorId = sampledValue?.connector;
          if (!connectorId) return;

          try {
            const response = (await getConnectorById(connectorId)) as
              | { data?: Connector }
              | undefined;
            if (response?.data) {
              void dispatch(fetchUpdatedConnector(response.data));
            }
          } catch (error) {
            log.error(error);
          }
        }
      };
    }

    return () => {
      if (synclogWebsocket) {
        synclogWebsocket.onmessage = null;
      }
    };
  }, [dispatch, firstRender, synclogWebsocket, chargePointsByGridConnectionId]);

  if (!user?.profile?.assets?.length && !user?.is_superuser) {
    return (
      <div className="h-100">
        <Title />
        <ViewSectionMessage message="Su usuario no tiene activos asociados. Contáctese con un administrador del sistema si cree que se trata de un error." />
      </div>
    );
  }

  // if there are no assets
  if (!assets?.length && assetsLoading === 'success') {
    return (
      <div className="h-100">
        <Title />
        <ViewSectionMessage message="No se han encontrado activos" />
      </div>
    );
  }

  // if assets have no grid connections
  if (
    !assets.filter((asset) => asset.grid_connections.length).length &&
    assetsLoading === 'success'
  ) {
    return (
      <div className="h-100">
        <Title />
        <ViewSectionMessage message="Los activos asociados no tienen empalmes disponibles. Contáctese con un administrador del sistema si cree que se trata de un error." />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-fill">
      <Title />
      <div className="d-flex flex-row justify-content-between align-items-start">
        <Tabs value={currentTab} className="$tw-w-full $tw-overflow-hidden $tw-rounded-lg">
          <TabsList className="$tw-bg-muted/50">
            <TabsTrigger
              value="charge-center"
              onClick={() => void navigate({ to: '/charge-center' })}
            >
              Monitor de empalmes
            </TabsTrigger>
            <TabsTrigger value="map" onClick={() => void navigate({ to: '/charge-center/map' })}>
              Mapa
            </TabsTrigger>
          </TabsList>
          <TabsContent value={currentTab}>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
